import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../api";
import { Link } from "react-router-dom";
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

export default function Dashboard() {
    const [stats, setStats] = useState({
        contacts: 0,
        sent: 0,
        failed: 0,
        pending: 0,
        balance: 0,
        campaigns: 0,
        deliveryRate: 0,
        growth: 0,
        breakdown: {
            delivered: 0,
            read: 0,
            undelivered: 0,
            rejected: 0,
            sent: 0
        },
        campaignsList: []
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Trigger auto-sync on load (background)
                axios.get(`${API}/sms/sync-all`).catch(e => console.warn("Auto-sync failed:", e));

                const [contactsRes, statsRes, campaignsRes] = await Promise.all([
                    axios.get(`${API}/contacts?t=${Date.now()}`),
                    axios.get(`${API}/sms/stats?t=${Date.now()}`),
                    axios.get(`${API}/campaigns?t=${Date.now()}`)
                ]);

                const contacts = contactsRes.data || [];
                const backendStats = statsRes.data || {};
                const campaigns = campaignsRes.data || [];

                const now = new Date();
                const lastMonth = new Date(now.setMonth(now.getMonth() - 1));
                const newContacts = contacts.filter(c => new Date(c.createdAt) > lastMonth).length;
                const oldContacts = contacts.length - newContacts;
                const growth = oldContacts > 0 ? ((newContacts / oldContacts) * 100).toFixed(1) : newContacts > 0 ? 100 : 0;

                const totalAttempts = backendStats.sent + backendStats.failed;
                const deliveryRate = totalAttempts > 0 ? ((backendStats.sent / totalAttempts) * 100).toFixed(1) : 0;

                setStats({
                    contacts: contacts.length,
                    sent: backendStats.sent || 0,
                    failed: backendStats.failed || 0,
                    pending: backendStats.pending || 0,
                    balance: backendStats.balance || 0,
                    campaigns: campaigns.length,
                    campaignsList: campaigns,
                    deliveryRate,
                    growth,
                    breakdown: {
                        delivered: backendStats.breakdown?.delivered || 0,
                        read: backendStats.breakdown?.read || 0,
                        undelivered: backendStats.breakdown?.undelivered || 0,
                        rejected: backendStats.breakdown?.rejected || 0,
                        sent: backendStats.breakdown?.sent || 0
                    }
                });
            } catch (err) {
                console.error("Error fetching stats", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const chartData = stats.campaignsList.slice(0, 10).reverse().map(c => ({
        name: c.name.length > 12 ? c.name.substring(0, 10) + '...' : c.name,
        sent: c.totalSent || 0,
        failed: c.failedCount || 0,
        successRate: c.totalMessages > 0 ? Math.round((c.totalSent / c.totalMessages) * 100) : 0
    }));

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="glass-panel" style={{ padding: "12px", border: "1px solid var(--glass-border)", background: "rgba(10, 10, 10, 0.9)" }}>
                    <p style={{ margin: "0 0 8px 0", fontWeight: "700", fontSize: "0.9rem" }}>{label}</p>
                    {payload.map((entry, index) => (
                        <div key={index} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.85rem", color: entry.color }}>
                            <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: entry.color }}></div>
                            <span>{entry.name}: {entry.value}</span>
                        </div>
                    ))}
                    <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid rgba(255,255,255,0.1)", fontSize: "0.8rem", color: "var(--success)" }}>
                        Success Rate: {payload[0].payload.successRate}%
                    </div>
                </div>
            );
        }
        return null;
    };

    const StatCard = ({ title, value, subtext, icon, trend, color }) => (
        <div className="glass-panel" style={{ padding: "28px", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
            <div style={{ position: "absolute", top: "-10px", right: "-10px", opacity: 0.05, color: color || "#fff" }}>
                {icon}
            </div>
            <div>
                <h3 style={{ margin: "0 0 12px 0", color: "var(--text-secondary)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: "600" }}>{title}</h3>
                <div style={{ fontSize: "2.2rem", fontWeight: "800", color: "#fff", marginBottom: "8px", letterSpacing: "-0.02em" }}>
                    {value}
                </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.85rem", marginTop: "auto" }}>
                {trend ? (
                    <span style={{ color: parseFloat(trend) >= 0 ? "var(--success)" : "var(--danger)", fontWeight: "700" }}>
                        {parseFloat(trend) >= 0 ? "+" : ""}{trend}%
                    </span>
                ) : null}
                <span style={{ color: "var(--text-muted)" }}>{subtext}</span>
            </div>
        </div>
    );

    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({ title: "", logs: [] });
    const [fetchingLogs, setFetchingLogs] = useState(false);

    const openLogsModal = async (title, statuses) => {
        setFetchingLogs(true);
        setModalData({ title, logs: [] });
        setShowModal(true);
        try {
            const res = await axios.get(`${API}/sms/logs?status=${statuses.join(",")}&t=${Date.now()}`);
            setModalData({ title, logs: res.data });
        } catch (err) {
            console.error("Error fetching modal logs", err);
        } finally {
            setFetchingLogs(false);
        }
    };

    return (
        <div className="page-container">
            {/* Modal for Detailed Logs */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)} style={{
                    position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
                    background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex",
                    justifyContent: "center", alignItems: "center", backdropFilter: "blur(8px)"
                }}>
                    <div className="glass-panel modal-content" onClick={e => e.stopPropagation()} style={{
                        width: "90%", maxWidth: "1000px", maxHeight: "80vh", overflow: "hidden",
                        display: "flex", flexDirection: "column", padding: "32px", border: "1px solid var(--glass-border)"
                    }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                            <h2 style={{ margin: 0 }}>{modalData.title}</h2>
                            <button onClick={() => setShowModal(false)} style={{
                                background: "none", border: "none", color: "var(--text-secondary)",
                                fontSize: "2rem", cursor: "pointer"
                            }}>&times;</button>
                        </div>
                        
                        <div style={{ flex: 1, overflowY: "auto", borderRadius: "12px", background: "rgba(0,0,0,0.2)" }}>
                            {fetchingLogs ? (
                                <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>Loading records...</div>
                            ) : modalData.logs.length > 0 ? (
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead style={{ position: "sticky", top: 0, background: "var(--bg-deep)", zIndex: 10 }}>
                                        <tr>
                                            <th style={{ padding: "16px", textAlign: "left" }}>Phone</th>
                                            <th style={{ padding: "16px", textAlign: "left" }}>Status</th>
                                            <th style={{ padding: "16px", textAlign: "left" }}>Reason</th>
                                            <th style={{ padding: "16px", textAlign: "right" }}>Time</th>
                                            <th style={{ padding: "16px", textAlign: "right" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modalData.logs.map((log, idx) => (
                                            <tr key={idx} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                                                <td style={{ padding: "12px 16px" }}>{log.phone}</td>
                                                <td style={{ padding: "12px 16px" }}>
                                                    <span className={`status-badge ${log.status.toLowerCase()}`}>{log.status}</span>
                                                </td>
                                                <td style={{ padding: "12px 16px", fontSize: "0.85rem", color: "var(--text-muted)" }}>{log.reason}</td>
                                                <td style={{ padding: "12px 16px", textAlign: "right", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                                                    {new Date(log.date).toLocaleString()}
                                                </td>
                                                <td style={{ padding: "12px 16px", textAlign: "right" }}>
                                                    {log.messageId && (
                                                        <button 
                                                            onClick={async (e) => {
                                                                const btn = e.currentTarget;
                                                                btn.disabled = true;
                                                                btn.innerText = "...";
                                                                try {
                                                                    const res = await axios.get(`${API}/sms/sync/${log.messageId}`);
                                                                    if (res.data.success) {
                                                                        alert(`Updated to ${res.data.status}`);
                                                                        window.location.reload();
                                                                    } else {
                                                                        alert(res.data.message);
                                                                    }
                                                                } catch (err) {
                                                                    alert("Sync failed: " + err.message);
                                                                } finally {
                                                                    btn.disabled = false;
                                                                    btn.innerText = "Sync";
                                                                }
                                                            }}
                                                            style={{
                                                                padding: "4px 8px", fontSize: "0.7rem", borderRadius: "4px",
                                                                background: "var(--glass-highlight)", color: "#fff", border: "1px solid var(--glass-border)",
                                                                cursor: "pointer"
                                                            }}
                                                        >Sync</button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>No records found for this category.</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="header-actions" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "32px" }}>
                <div>
                    <h1 style={{ marginBottom: "4px" }}>System Overview</h1>
                    <p style={{ color: "var(--text-secondary)", margin: 0 }}>Real-time analytics and performance metrics for your broadcasts.</p>
                </div>
                <div className="header-btns" style={{ display: "flex", gap: "12px" }}>
                    <button 
                        onClick={async (e) => {
                            const btn = e.currentTarget;
                            btn.disabled = true;
                            const oldText = btn.innerText;
                            btn.innerText = "Syncing...";
                            try {
                                const res = await axios.get(`${API}/sms/sync-all`);
                                alert(res.data.message);
                                window.location.reload();
                            } catch (err) {
                                alert("Sync failed: " + err.message);
                            } finally {
                                btn.disabled = false;
                                btn.innerText = oldText;
                            }
                        }}
                        className="glass-input" style={{ width: "auto", height: "44px", fontSize: "0.9rem", padding: "0 20px", cursor: "pointer", fontWeight: "600" }}
                    >
                        🔄 Sync All
                    </button>
                    <Link to="/sms" style={{ textDecoration: "none" }}>
                        <button className="btn-primary" style={{ padding: "0 20px", height: "44px", fontSize: "0.9rem" }}>
                            🚀 New Campaign
                        </button>
                    </Link>
                </div>
            </div>

            <div className="stat-grid">
                <StatCard 
                    title="Total Audience" 
                    value={stats.contacts} 
                    subtext="Verified Contacts" 
                    trend={stats.growth}
                    icon={<svg width="100" height="100" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>}
                />
                <StatCard 
                    title="Successfully Sent" 
                    value={stats.sent} 
                    subtext={`${stats.deliveryRate}% Success Rate`} 
                    color="var(--success)"
                    icon={<svg width="100" height="100" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>}
                />
                <StatCard 
                    title="Failed Delivery" 
                    value={stats.failed} 
                    subtext="Action Required" 
                    color="var(--danger)"
                    icon={<svg width="100" height="100" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/></svg>}
                />
                <StatCard 
                    title="Credit Balance" 
                    value={`€${parseFloat(stats.balance || 0).toFixed(2)}`} 
                    subtext="Remaining Funds" 
                    color="var(--accent-primary)"
                    icon={<svg width="100" height="100" viewBox="0 0 24 24" fill="currentColor"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>}
                />
                <StatCard 
                    title="Active Queue" 
                    value={stats.pending} 
                    subtext="Messages Processing" 
                    color="var(--warning)"
                    icon={<svg width="100" height="100" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>}
                />
            </div>

            <div className="analytics-grid">
                {/* Left Side: Smart Analytics Chart */}
                <div className="glass-panel" style={{ padding: "32px", height: "480px", display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: "700" }}>Performance Analytics</h3>
                            <p style={{ margin: "4px 0 0 0", fontSize: "0.8rem", color: "var(--text-muted)" }}>Visual breakdown of your recent broadcast effectiveness.</p>
                        </div>
                        <div style={{ display: "flex", gap: "16px", fontSize: "0.75rem" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--success)" }}></div>Sent</div>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--danger)" }}></div>Failed</div>
                        </div>
                    </div>
                    
                    <div style={{ flex: 1, width: "100%" }}>
                        <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                            <AreaChart data={chartData}>
                                <defs>
                                    <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="var(--success)" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="var(--success)" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="var(--danger)" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="var(--danger)" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis 
                                    dataKey="name" 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{ fill: 'var(--text-muted)', fontSize: 11 }} 
                                    dy={10}
                                />
                                <YAxis 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{ fill: 'var(--text-muted)', fontSize: 11 }} 
                                />
                                <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }} />
                                <Area 
                                    type="monotone" 
                                    dataKey="sent" 
                                    stroke="var(--success)" 
                                    strokeWidth={3}
                                    fillOpacity={1} 
                                    fill="url(#colorSent)" 
                                    animationDuration={1500}
                                />
                                <Area 
                                    type="monotone" 
                                    dataKey="failed" 
                                    stroke="var(--danger)" 
                                    strokeWidth={3}
                                    fillOpacity={1} 
                                    fill="url(#colorFailed)" 
                                    animationDuration={1500}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Right Side: Delivery Status */}
                <div className="glass-panel" style={{ padding: "32px", minHeight: "480px", display: "flex", flexDirection: "column", height: "100%" }}>
                    <h3 style={{ margin: "0 0 24px 0", fontSize: "1.25rem", fontWeight: "700" }}>Delivery Status</h3>
                    
                    <div style={{ display: "flex", flexDirection: "column", gap: "24px", flex: 1, justifyContent: "center" }}>
                        {[
                            { label: "Sent / Delivered", value: (stats.breakdown.delivered || 0) + (stats.breakdown.sent || 0), color: "var(--success)", icon: "✓", statuses: ["SENT", "DELIVERED", "SUCCESS", "ACCEPTED", "TRANSMITTED"] },
                            { label: "Link Clicked / Read", value: stats.breakdown.read, color: "var(--accent-primary)", icon: "👁", statuses: ["READ"] },
                            { label: "Network Failures", value: stats.breakdown.undelivered, color: "var(--danger)", icon: "!", statuses: ["UNDELIVERED", "EXPIRED", "INSUFFICIENT FUNDS", "ERROR"] },
                            { label: "Rejected / Blocked", value: stats.breakdown.rejected, color: "rgba(239, 68, 68, 0.5)", icon: "×", statuses: ["REJECTED", "RECIPIENT NUMBER INVALID", "BLACKLISTED"] }
                        ].map((item, idx) => (
                            <div key={idx} 
                                 onClick={() => openLogsModal(item.label, item.statuses)}
                                 style={{ display: "flex", alignItems: "center", gap: "16px", cursor: "pointer", padding: "12px", borderRadius: "12px", transition: "background 0.2s" }}
                                 className="hover-highlight"
                            >
                                <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: "var(--bg-deep)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem", color: item.color }}>
                                    {item.icon}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                                        <span style={{ fontSize: "0.9rem", color: "var(--text-secondary)", fontWeight: "600" }}>{item.label}</span>
                                        <span style={{ fontSize: "1rem", fontWeight: "800", color: "#fff" }}>{item.value}</span>
                                    </div>
                                    <div style={{ width: "100%", background: "var(--bg-deep)", height: "8px", borderRadius: "4px", overflow: "hidden" }}>
                                        <div style={{ 
                                            width: `${stats.sent + stats.failed > 0 ? (item.value / (stats.sent + stats.failed) * 100) : 0}%`, 
                                            height: "100%", 
                                            background: item.color,
                                            transition: "width 1s ease" 
                                        }}></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: "auto", paddingTop: "24px", borderTop: "1px solid var(--glass-border)" }}>
                        <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginBottom: "8px" }}>Quick Insights</div>
                        <p style={{ margin: 0, fontSize: "0.85rem", lineHeight: "1.5", color: "var(--text-muted)" }}>
                            Your current delivery success rate is <strong>{stats.deliveryRate}%</strong>. 
                        </p>
                    </div>
                </div>
            </div>

            {/* Performance History Table */}
            <div className="glass-panel" style={{ padding: "32px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                    <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: "700" }}>Recent Campaign Performance</h3>
                    <Link to="/active-campaigns" style={{ color: "var(--accent-primary)", fontSize: "0.85rem", fontWeight: "600", textDecoration: "none" }}>Manage All Campaigns →</Link>
                </div>
                <div className="table-responsive">
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={{ padding: "16px", textAlign: "left" }}>Campaign Name</th>
                                <th style={{ padding: "16px", textAlign: "left" }}>Status</th>
                                <th style={{ padding: "16px", textAlign: "left" }}>List Used</th>
                                <th style={{ padding: "16px", textAlign: "left" }}>Success Rate</th>
                                <th style={{ padding: "16px", textAlign: "right" }}>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stats.campaignsList.length > 0 ? (
                                stats.campaignsList.slice(0, 6).map((c) => {
                                    const successRate = c.totalMessages > 0 ? Math.round((c.totalSent / c.totalMessages) * 100) : 0;
                                    return (
                                        <tr key={c._id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                                            <td style={{ padding: "16px", fontWeight: "600", color: "#fff" }}>{c.name}</td>
                                            <td style={{ padding: "16px" }}>
                                                <span style={{ fontSize: "0.75rem", fontWeight: "700", color: c.status === "COMPLETED" ? "var(--success)" : "var(--warning)" }}>
                                                    {c.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: "16px", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
                                                {c.listName || "All Contacts"}
                                            </td>
                                            <td style={{ padding: "16px" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <div style={{ width: "100px", background: "var(--bg-deep)", height: "6px", borderRadius: "3px", overflow: "hidden" }}>
                                                        <div style={{ width: `${successRate}%`, height: "100%", background: "var(--success)", borderRadius: "3px" }}></div>
                                                    </div>
                                                    <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>{successRate}%</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: "16px", textAlign: "right", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                                                {new Date(c.date).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr><td colSpan="4" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>No campaigns initiated yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
